import { useCallback, useEffect, useState } from "react";
import RadioInput from "../inputs/RadioInput";
import { CreditDebit, DirectDebit } from "../payment";
import Separator from "./Separator";
import {
	PaymentMethodLabel,
	getDigitalWallets,
} from "../payment/DigitalWallets";
// import { initializePaypal } from "../payment/PayPal";

const Payment = ({
	register,
	unregister,
	watch,
	setValue,
	errors,
	field,
	error,
	required = false,
	id,
	formData,
	config,
	trigger,
	paymentMethods,
	setPaymentMethods,
}) => {
	//set initial payment methods (maybe frequency and amount arent in form)

	const selectedPaymentMethodValue = watch(id);

	// watch for frequency changes if it exists, and filter out payment methods that are not enabled for that frequency
	const amount = watch("donation_frequency_and_amount[amount]");
	const frequency = watch("donation_frequency_and_amount[frequency]");

	//render digital wallets when frequency and amount change
	useEffect(() => {
		let paymentMethods = field.methods.filter(
			(method) => method.frequency_flag === frequency
		);

		setPaymentMethods(paymentMethods);

		//if there are payment methods, select the first one
		if (paymentMethods.length > 0) {
			paymentMethods[0].selected = true;
			setValue(id, paymentMethods[0].type);
		}

		//ser tloading for digital wallets and paypal
		paymentMethods.forEach((method) => {
			if (method.type === "digital_wallet") {
				method.loading = true;
			}
		});

		async function renderDigitalWallets() {
			//if no payment method selected, select first available one
			let digitalWalletsToAdd = [];
			// initialize digital wallets methods
			await Promise.all(
				paymentMethods.map(async (method) => {
					if (method.type === "digital_wallet") {
						const wallets = await getDigitalWallets(
							config.stripe_public_key,
							amount
						);
						wallets?.forEach((wallet) => {
							digitalWalletsToAdd.push({
								...method,
								loading: false,
								type: "digital_wallet",
								digital_wallet_type: wallet.type,
								triggerType: "digital_wallet",
								label: <PaymentMethodLabel type={wallet.type} />,
								pr: wallet.pr,
								isSimulated: wallet.isSimulated, // Pass through simulation flag
							});
						});
					}
				})
			);

			//if frequency remains the same, and the selected payment method is digital wallet, set loading to true
			const newFrequency = watch("donation_frequency_and_amount[frequency]");
			const newAmount = watch("donation_frequency_and_amount[amount]");
			if (frequency !== newFrequency || amount !== newAmount) {
				return;
			}

			// Find index of digital_wallet placeholder
			const digitalWalletIndex = paymentMethods.findIndex(
				(method) => method.type === "digital_wallet"
			);

			// Remove digital wallet placeholder
			paymentMethods = paymentMethods.filter(
				(method) => method.type !== "digital_wallet"
			);

			// Insert any found digital wallets at the original position
			if (digitalWalletIndex >= 0) {
				paymentMethods.splice(digitalWalletIndex, 0, ...digitalWalletsToAdd);
			}

			if (paymentMethods.length > 0) {
				paymentMethods[0].selected = true;
				setValue(id, paymentMethods.find((method) => method.selected)?.type);
			}

			setPaymentMethods(paymentMethods);
		}
		async function renderPaypal() {
			try {
				// Initialize PayPal and get buttons config
				// const paypalButtonsConfig = await initializePaypal(
				// 	config.paypal_client_id,
				// 	amount
				// );

				// Update PayPal method with the buttons config
				paymentMethods.forEach((method) => {
					if (method.type === "paypal") {
						method.label = <PaymentMethodLabel type="paypal" />;
						method.triggerType = "paypal";
						method.loading = false;
						method.amount = amount;
						method.currency = "GBP";
						method.paypalPayment = paypalButtonsConfig; // Similar to pr for digital wallets
					}
				});

				setPaymentMethods([...paymentMethods]);
			} catch (error) {
				console.error("Failed to initialize PayPal:", error);
				// Handle PayPal initialization error
				paymentMethods.forEach((method) => {
					if (method.type === "paypal") {
						method.loading = false;
						method.disabled = true;
					}
				});
				setPaymentMethods([...paymentMethods]);
			}
		}

		renderDigitalWallets();
		renderPaypal();
	}, [amount, frequency]);

	const getPaymentComponent = useCallback(
		(methodValue) => {
			const method = paymentMethods?.find((m) => m.type === methodValue);
			if (!method) return null;

			const commonProps = {
				register,
				unregister,
				watch,
				setValue,
				errors,
				id,
				error,
				config,
				trigger,
				...method,
			};

			switch (method.type) {
				case "direct_debit":
					return <DirectDebit key={method.type} {...commonProps} />;
				case "credit_debit":
					return <CreditDebit key={method.type} {...commonProps} />;
				case "paypal":
				case "googlepay":
				case "applepay":
				case "digital_wallet":
				case "button":
				default:
					return false;
			}
		},
		[paymentMethods]
	);

	if (paymentMethods?.length < 1 && field.hide_options_if_only_one) return null;

	const paymentComponent = getPaymentComponent(selectedPaymentMethodValue);

	return (
		<>
			{(!field.hide_options_if_only_one ||
				(field.hide_options_if_only_one && paymentMethods?.length > 1)) && (
				<div className="container-wide field-section-wrapper">
					<RadioInput
						style="boxes"
						hide_options_if_only_one={field.hide_options_if_only_one}
						options={paymentMethods?.map((method) => ({
							value: method.type,
							label: method.label,
							image: method.image,
							description: method.description,
							loading: method.loading,
							disabled: method.loading,
						}))}
						id={id}
						required={required}
						register={register}
					/>
				</div>
			)}

			{paymentComponent && (
				<>
					{!config.config.hide_separators && (
						<Separator separator_type="wide" />
					)}
					<div className="container field-section-wrapper">
						<div className="field__payment-component container field-section-wrapper">
							{paymentComponent}
						</div>
					</div>
				</>
			)}
		</>
	);
};

export default Payment;
